import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { FORMATIONS, DRAW_COLORS } from "../lib/constants";
import PitchBoard from "../components/PitchBoard";
import { toast } from "sonner";
import {
  CirclePlus, Trash, Target, SaveAll, Move, Minus, ArrowUpRight, Undo, Eraser, LoaderCircle,
} from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[#f7fdfa] border border-[#d1fae5] focus:border-[#2a3b19] outline-none text-sm";

const DRAW_TOOLS = [
  { key: "none", label: "تحريك", icon: Move },
  { key: "arrow", label: "سهم تحرك", icon: ArrowUpRight },
  { key: "line", label: "خط", icon: Minus },
  { key: "dotted", label: "تمريرة", icon: Minus },
];

export default function Tactics() {
  const [tactics, setTactics] = useState([]);
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [formation, setFormation] = useState("4-3-3");
  const [positions, setPositions] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [drawMode, setDrawMode] = useState("none");
  const [drawColor, setDrawColor] = useState(DRAW_COLORS[0].value);
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/tactics"), api.get("/players")]);
    setTactics(t.data);
    setPlayers(p.data);
  };
  useEffect(() => { load(); }, []);

  const buildFrom = (f, existing) => {
    const coords = FORMATIONS[f];
    return coords.map((c, i) => {
      const pl = players[i];
      const prev = existing?.[i];
      return {
        player_id: prev?.player_id ?? pl?.id ?? null,
        label: prev?.label ?? pl?.name ?? `مركز ${i + 1}`,
        number: prev?.number ?? pl?.jersey_number ?? i + 1,
        x: c.x, y: c.y,
        instructions: prev?.instructions ?? { attack: "", defense: "", note: "" },
      };
    });
  };

  const applyFormation = (f) => {
    setFormation(f);
    setPositions(buildFrom(f, positions));
  };

  const newBoard = () => {
    setEditingId(null);
    setName("");
    setNotes("");
    setDrawings([]);
    setDrawMode("none");
    setFormation("4-3-3");
    setPositions(buildFrom("4-3-3", null));
    setSelectedId(null);
  };

  useEffect(() => {
    if (players.length && positions.length === 0 && !editingId) {
      setPositions(buildFrom("4-3-3", null));
    }
    // eslint-disable-next-line
  }, [players]);

  const moveToken = (id, x, y) =>
    setPositions((ps) => ps.map((p) => p.player_id === id ? { ...p, x, y } : (p.player_id == null && `slot-${ps.indexOf(p)}` === id ? { ...p, x, y } : p)));

  const tokens = positions.map((p, i) => ({
    id: p.player_id || `slot-${i}`,
    number: p.number, name: p.label,
    status: players.find((pl) => pl.id === p.player_id)?.status,
    x: p.x, y: p.y,
  }));

  const moveAny = (id, x, y) =>
    setPositions((ps) => ps.map((p, i) => ((p.player_id || `slot-${i}`) === id ? { ...p, x, y } : p)));

  const selected = positions.find((p, i) => (p.player_id || `slot-${i}`) === selectedId);
  const setInstr = (field, val) =>
    setPositions((ps) => ps.map((p, i) => (p.player_id || `slot-${i}`) === selectedId ? { ...p, instructions: { ...p.instructions, [field]: val } } : p));

  const save = async () => {
    if (!name.trim()) { toast.error("أدخل اسم التشكيلة"); return; }
    setSaving(true);
    const payload = { name, formation, positions, drawings, notes };
    try {
      if (editingId) await api.put(`/tactics/${editingId}`, payload);
      else await api.post("/tactics", payload);
      toast.success("تم حفظ التشكيلة بنجاح");
      newBoard();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const loadTactic = (t) => {
    setEditingId(t.id);
    setName(t.name);
    setFormation(t.formation);
    setPositions(t.positions || []);
    setDrawings(t.drawings || []);
    setNotes(t.notes || "");
    setDrawMode("none");
    setSelectedId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("حذف التشكيلة؟")) return;
    await api.delete(`/tactics/${id}`);
    if (editingId === id) newBoard();
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-[#2a3b19] uppercase tracking-wider">الاستوديو التكتيكي</p>
          <h1 className="text-3xl font-black">التشكيلات والخطط</h1>
        </div>
        <button onClick={newBoard} data-testid="new-tactic-btn" className="px-5 py-3 rounded-xl bg-[#ecfdf5] hover:bg-[#d1fae5] font-medium flex items-center gap-2">
          <CirclePlus size={18} /> تشكيلة جديدة
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 glass border border-[#d1fae5] rounded-2xl p-5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم التشكيلة (مثال: ضغط عالي 4-3-3)" className={inputCls + " mb-3"} data-testid="tactic-name" />

          <div className="flex flex-wrap gap-2 mb-3">
            {Object.keys(FORMATIONS).map((f) => (
              <button key={f} onClick={() => applyFormation(f)} data-testid={`tf-${f}`} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${formation === f ? "bg-[#2a3b19] text-white" : "bg-[#ecfdf5] text-slate-700"}`}>{f}</button>
            ))}
          </div>

          {/* draw tools */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {DRAW_TOOLS.map((t) => (
              <button key={t.key} onClick={() => setDrawMode(t.key)} data-testid={`tool-${t.key}`} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 ${drawMode === t.key ? "bg-[#2a3b19] text-white" : "bg-[#ecfdf5] text-slate-700"}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
            <div className="flex gap-1 mr-auto">
              {DRAW_COLORS.map((c) => (
                <button key={c.key} onClick={() => setDrawColor(c.value)} className={`w-6 h-6 rounded-full border-2 ${drawColor === c.value ? "border-white" : "border-transparent"}`} style={{ background: c.value }} data-testid={`color-${c.key}`} />
              ))}
            </div>
            <button onClick={() => setDrawings((d) => d.slice(0, -1))} className="p-1.5 rounded-lg bg-[#ecfdf5]" data-testid="undo-btn"><Undo size={15} /></button>
            <button onClick={() => setDrawings([])} className="p-1.5 rounded-lg bg-[#ecfdf5]" data-testid="clear-btn"><Eraser size={15} /></button>
          </div>

          <PitchBoard
            players={tokens}
            onMove={moveAny}
            draggable
            drawMode={drawMode}
            drawColor={drawColor}
            drawings={drawings}
            onDrawingsChange={setDrawings}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <p className="text-xs text-slate-500 mt-2 text-center">اسحب اللاعبين لمراكزهم · اختر أداة الرسم لرسم التحركات والكرات الثابتة</p>

          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ملاحظات عامة على الخطة والتكتيك" className={inputCls + " mt-3"} rows={2} data-testid="tactic-notes" />

          <button onClick={save} disabled={saving} data-testid="save-tactic-btn" className="w-full mt-3 py-3 rounded-xl bg-[#2a3b19] hover:bg-[#3a4f22] text-white font-bold flex items-center justify-center gap-2">
            {saving ? <LoaderCircle className="animate-spin" size={18} /> : <SaveAll size={18} />} حفظ التشكيلة
          </button>
        </div>

        <div className="space-y-5">
          {/* per-player instructions */}
          <div className="glass border border-[#d1fae5] rounded-2xl p-5">
            <h3 className="font-bold mb-3 flex items-center gap-2"><Target size={16} className="text-[#2a3b19]" /> تعليمات فردية</h3>
            {selected ? (
              <div className="space-y-2">
                <div className="text-sm font-medium text-[#2a3b19]">{selected.label}</div>
                <input value={selected.instructions?.attack || ""} onChange={(e) => setInstr("attack", e.target.value)} placeholder="الدور في الهجوم" className={inputCls} data-testid="instr-attack" />
                <input value={selected.instructions?.defense || ""} onChange={(e) => setInstr("defense", e.target.value)} placeholder="الدور في الدفاع" className={inputCls} data-testid="instr-defense" />
                <input value={selected.instructions?.note || ""} onChange={(e) => setInstr("note", e.target.value)} placeholder="تعليمات إضافية" className={inputCls} data-testid="instr-note" />
              </div>
            ) : (
              <p className="text-sm text-slate-500">اضغط على لاعب في الملعب لإضافة تعليماته.</p>
            )}
          </div>

          {/* saved tactics */}
          <div className="glass border border-[#d1fae5] rounded-2xl p-5">
            <h3 className="font-bold mb-3">التشكيلات المحفوظة ({tactics.length})</h3>
            <div className="space-y-2">
              {tactics.map((t) => (
                <div key={t.id} data-testid={`saved-tactic-${t.id}`} className={`flex items-center justify-between rounded-xl px-3 py-2.5 border ${editingId === t.id ? "border-[#2a3b19] bg-[#2a3b19]/10" : "border-[#d1fae5] bg-[#f7fdfa]"}`}>
                  <button onClick={() => loadTactic(t)} className="text-right flex-1">
                    <div className="font-medium text-sm">{t.name}</div>
                    <div className="text-xs text-[#2a3b19]">{t.formation}</div>
                  </button>
                  <button onClick={() => remove(t.id)} className="text-red-500 hover:text-red-300" data-testid={`delete-tactic-${t.id}`}><Trash size={16} /></button>
                </div>
              ))}
              {tactics.length === 0 && <p className="text-sm text-slate-500">لا توجد تشكيلات محفوظة.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
